import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'

import { getUserAccesiblePages } from 'app/selectors'
import ContractVersionNotAllowedPageMessage from 'components/Auth/ContractVersionNotAllowedPageMessage'
import { checkFeatureFlag } from 'app/config'

const PageAccessVerification = ({
  children,
  currentUrlData,
  availablePages,
}) => {
  const [isAllowed, setIsAllowed] = useState(false)
  const [isChecking, setIsChecking] = useState(true) // Add loading state
  const userPages = useSelector(getUserAccesiblePages)
  const router = useRouter()

  // Helper function to extract current page from pathname
  const getCurrentPageFromPath = (pathname) => {
    return pathname.substring(1)
  }

  // Helper function to find the current page object
  const findCurrentPageObject = (currentPage, availablePages) => {
    return availablePages.find(page => page.page === currentPage)
  }

  const hasPageAccess = (currentPage, availablePages) => {
    const currentPageObject = findCurrentPageObject(currentPage, availablePages)

    if (!currentPageObject) {
      return false
    }

    if (
      currentPageObject?.privacy === 'PUBLIC' ||
      (currentPageObject?.privacy === 'PRIVATE' &&
        currentPageObject?.skipContractVersionVerification)
    ) {
      return true
    }

    return userPages.find(page => page.name === currentPageObject.pageName)
  }

  const checkUserPageAccess = ({
    availablePages = [],
    currentUrlData = {},
  }) => {
    const { pathname } = currentUrlData
    const currentPage = getCurrentPageFromPath(pathname)
    return availablePages.length === 0
      ? true
      : hasPageAccess(currentPage, availablePages)
  }

  const isPageHiddenByFeatureFlag = (currentPage, availablePages) => {
    const currentPageObject = findCurrentPageObject(currentPage, availablePages)

    if (!currentPageObject) {
      return false
    }

    // Check if page has feature flag conditions
    if (
      currentPageObject.ff &&
      Array.isArray(currentPageObject.ff) &&
      currentPageObject.ff.length > 0
    ) {
      // Check if any feature flag condition is not met
      return currentPageObject.ff.some(featureFlagConfig => {
        const actualFeatureFlagValue = checkFeatureFlag(featureFlagConfig.name)
        // Page should be hidden if the actual value doesn't match the expected value
        return actualFeatureFlagValue !== featureFlagConfig.value
      })
    }

    return false
  }

  useEffect(() => {
    const { pathname } = currentUrlData
    const currentPage = getCurrentPageFromPath(pathname)

    // Check if page should be hidden based on feature flags
    if (isPageHiddenByFeatureFlag(currentPage, availablePages)) {
      router.push('/404')
      return
    }

    setIsAllowed(
      checkUserPageAccess({
        availablePages,
        currentUrlData,
      }),
    )
    setIsChecking(false) // Mark checking as complete
  }, [currentUrlData, availablePages, router])

  // Don't render anything while checking to prevent flash of error message
  if (isChecking) {
    return null // or return a loading spinner if preferred
  }

  return isAllowed ? children : <ContractVersionNotAllowedPageMessage />
}

PageAccessVerification.propTypes = {
  children: PropTypes.any,
  currentUrlData: PropTypes.object,
  availablePages: PropTypes.array,
}

export default PageAccessVerification
